'use client';

import React from 'react';
import { useTranslation } from 'next-i18next';
import { BestieWelcome } from './BestieWelcome';
import { HowItWorks } from './HowItWorks';
import { PermissionStatusBanner } from './PermissionStatusBanner';
import { PermissionActions } from './PermissionActions';
import { AudioPermissionState } from '../types';

interface InterviewLandingPageProps {
  permissionState: AudioPermissionState;
  onRequestPermission: () => void;
  onStartInterview: () => void;
}

/**
 * InterviewLandingPage
 *
 * Main landing component for the audio interview flow.
 *
 * This component renders the main hero section with the interview title,
 * the left-side preview of Bestie with a welcoming message,
 * the right-side "how it works" steps for the interview, and,
 * at the bottom, banners and actions related to microphone permissions.
 *
 * Props:
 * - permissionState: one of 'idle' | 'requesting' | 'granted' | 'denied'; tracks the mic permission state.
 * - onRequestPermission: function to call to request mic permission.
 * - onStartInterview: function to call to proceed to the interview.
 */
export const InterviewLandingPage: React.FC<InterviewLandingPageProps> = ({
  permissionState,
  onRequestPermission,
  onStartInterview,
}) => {
  const { t } = useTranslation('common');

  return (
    <div className="flex min-h-[600px] flex-col">
      {/* Hero Section */}
      <div className="mb-12 text-center">
        <h1 className="mb-4 text-4xl font-bold text-ink">
          {t('audio-interview.title')}
        </h1>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col items-center justify-center gap-12 lg:flex-row">
        {/* Left Side - Bestie Preview */}
        <BestieWelcome />

        {/* Right Side - How It Works */}
        <HowItWorks />
      </div>

      {/* Footer - Permission Status and Actions */}
      <div className="mt-12 space-y-6 text-center">
        <PermissionStatusBanner permissionState={permissionState} />
        <PermissionActions
          permissionState={permissionState}
          onRequestPermission={onRequestPermission}
          onStartInterview={onStartInterview}
        />
      </div>
    </div>
  );
};

